import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from '../../hooks/useTranslation';
import { BannerText, CreateBannerTextDto } from '../../types/bannerText';
import api from '../../utils/api';

export const BannerTextsManagement: React.FC = () => {
  const { t } = useTranslation();
  const [bannerTexts, setBannerTexts] = useState<BannerText[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateBannerTextDto>({
    text: '',
    endDate: undefined
  });
  const [errors, setErrors] = useState<Partial<CreateBannerTextDto>>({});

  useEffect(() => {
    fetchBannerTexts();
  }, []);

  const fetchBannerTexts = async () => {
    try {
      const response = await api.get<BannerText[]>('/banner-texts');
      setBannerTexts(response.data);
    } catch (error) {
      console.error('Error fetching banner texts:', error);
    }
  };

  const handleOpenDialog = (bannerText?: BannerText) => {
    if (bannerText) {
      setEditingId(bannerText._id);
      setFormData({
        text: bannerText.text,
        endDate: bannerText.endDate
      });
    } else {
      setEditingId(null);
      setFormData({ text: '', endDate: undefined });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({ text: '', endDate: undefined });
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof CreateBannerTextDto]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors: Partial<CreateBannerTextDto> = {};
    if (!formData.text.trim()) {
      newErrors.text = t('admin.bannerTexts.errors.textRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      if (editingId) {
        await api.put(`/banner-texts/${editingId}`, formData);
      } else {
        await api.post('/banner-texts', formData);
      }
      handleCloseDialog();
      fetchBannerTexts();
    } catch (error) {
      console.error('Error saving banner text:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.bannerTexts.confirmDelete'))) return;

    try {
      await api.delete(`/banner-texts/${id}`);
      fetchBannerTexts();
    } catch (error) {
      console.error('Error deleting banner text:', error);
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" component="h2">
          {t('admin.bannerTexts.title')}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenDialog()}
        >
          {t('admin.bannerTexts.addNew')}
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('admin.bannerTexts.text')}</TableCell>
              <TableCell>{t('admin.bannerTexts.endDate')}</TableCell>
              <TableCell align="right">{t('admin.bannerTexts.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bannerTexts.map((bannerText) => (
              <TableRow key={bannerText._id}>
                <TableCell>{bannerText.text}</TableCell>
                <TableCell>
                  {bannerText.endDate ? formatDate(bannerText.endDate) : t('admin.bannerTexts.noEndDate')}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    color="primary"
                    onClick={() => handleOpenDialog(bannerText)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleDelete(bannerText._id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editingId ? t('admin.bannerTexts.edit') : t('admin.bannerTexts.addNew')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label={t('admin.bannerTexts.text')}
              name="text"
              value={formData.text}
              onChange={handleInputChange}
              error={!!errors.text}
              helperText={errors.text}
              margin="normal"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label={t('admin.bannerTexts.endDate')}
              name="endDate"
              type="date"
              value={formData.endDate ? new Date(formData.endDate).toISOString().split('T')[0] : ''}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="contained" color="primary">
              {t('common.save')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
}; 